import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  Spinner,
  Tab,
  Nav,
  Alert,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faRobot,
  faBolt,
  faShieldAlt,
  faEnvelope,
  faPhone,
  faGlobe,
  faCheckCircle,
  faArrowRight,
  faStar,
  faComments,
  faBrain,
  faChartPie,
  faUserShield,
  faRocket,
  faLightbulb,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { ApiConfig } from "config/ApiConfig";
import { useAuth } from "context/AuthContext";
import logo from "assets/img/logos/TopCXLogo/logo.png";
import "./HomePage.css";

// Country codes for phone input
const countryCodes = [
  { code: "+1", country: "US/CA" },
  { code: "+44", country: "UK" },
  { code: "+91", country: "IN" },
  { code: "+61", country: "AU" },
  { code: "+49", country: "DE" },
  { code: "+33", country: "FR" },
  { code: "+81", country: "JP" },
  { code: "+86", country: "CN" },
  { code: "+65", country: "SG" },
  { code: "+971", country: "UAE" },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { setZendeskSubdomain, checkAuthenticationStatus } = useAuth();

  // Free tier limit - fetched from API or default
  const [freeTierLimit, setFreeTierLimit] = useState(100);

  // Active tab state
  const [activeTab, setActiveTab] = useState("login");

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  // Register form state
  const [registerForm, setRegisterForm] = useState({
    zendeskSubdomain: "",
    email: "",
    countryCode: "+1",
    phoneNumber: "",
  });

  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: "",
  });

  // Found subdomain for login
  const [foundSubdomain, setFoundSubdomain] = useState("");
  const [lookingUpEmail, setLookingUpEmail] = useState(false);

  // Fetch free tier limit on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(ApiConfig.getConfig || "/api/config");
        if (response.data?.freeTierLimit) {
          setFreeTierLimit(response.data.freeTierLimit);
        }
      } catch (error) {
        // Use default if API fails
        console.log("Using default free tier limit");
      }
    };
    fetchConfig();
  }, []);

  // Email lookup for login - find subdomain by email
  const lookupEmailForLogin = async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFoundSubdomain("");
      return;
    }

    setLookingUpEmail(true);
    try {
      const response = await axios.post(ApiConfig.lookupEmail || "/company/lookup-email", {
        email: email.toLowerCase(),
      });
      if (response.data?.subdomain) {
        setFoundSubdomain(response.data.subdomain);
      } else {
        setFoundSubdomain("");
      }
    } catch (error) {
      setFoundSubdomain("");
    } finally {
      setLookingUpEmail(false);
    }
  };

  // Debounced email lookup
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loginForm.email) {
        lookupEmailForLogin(loginForm.email);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [loginForm.email]);

  // Handle Register Submit
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!registerForm.zendeskSubdomain) {
      toast.error("Please enter your Zendesk subdomain", { theme: "colored" });
      return;
    }

    if (!/^[a-z0-9-]+$/.test(registerForm.zendeskSubdomain)) {
      toast.error("Invalid subdomain. Use only lowercase letters, numbers, and dashes", { theme: "colored" });
      return;
    }

    if (!registerForm.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerForm.email)) {
      toast.error("Please enter a valid email address", { theme: "colored" });
      return;
    }

    if (!registerForm.phoneNumber || !/^\d{6,15}$/.test(registerForm.phoneNumber)) {
      toast.error("Please enter a valid phone number", { theme: "colored" });
      return;
    }

    setIsLoading(true);

    try {
      const signupData = {
        email: registerForm.email.toLowerCase(),
        subdomain: registerForm.zendeskSubdomain.toLowerCase(),
        phone: `${registerForm.countryCode}${registerForm.phoneNumber}`,
        crm_name: "zendesk",
        source: "registration",
      };

      const response = await axios.post(ApiConfig.signup || "/company/signup", signupData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      if (response.data.redirectUrl) {
        setZendeskSubdomain(registerForm.zendeskSubdomain);

        // Open Zendesk OAuth popup
        const popup = window.open(
          response.data.redirectUrl,
          "zendesk_auth",
          "width=600,height=700,scrollbars=yes,resizable=yes,top=100,left=100"
        );

        window.zendeskAuthPopup = popup;
        setPopupOpen(true);

        // Monitor popup closure
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            setPopupOpen(false);
            handleAuthCheck(0, registerForm.zendeskSubdomain);
          }
        }, 500);

        toast.info("Zendesk login window opened. Please login with your Zendesk admin credentials.", {
          theme: "colored",
          autoClose: 8000,
        });
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.message || "Registration failed. Please try again.", {
        theme: "colored",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Login Submit
  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    if (!loginForm.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginForm.email)) {
      toast.error("Please enter a valid email address", { theme: "colored" });
      return;
    }

    if (!foundSubdomain) {
      toast.error("No account found with this email. Please register first.", { theme: "colored" });
      return;
    }

    setIsLoading(true);

    try {
      const loginData = {
        email: loginForm.email.toLowerCase(),
        subdomain: foundSubdomain,
        crm_name: "zendesk",
        id: "web_login",
        role: "admin",
        source: "admin_screen",
        user_auth: true,
      };

      const response = await axios.post(ApiConfig.login, loginData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      if (response.data.redirectUrl) {
        setZendeskSubdomain(foundSubdomain);

        const popup = window.open(
          response.data.redirectUrl,
          "zendesk_auth",
          "width=600,height=700,scrollbars=yes,resizable=yes,top=100,left=100"
        );

        window.zendeskAuthPopup = popup;
        setPopupOpen(true);

        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            setPopupOpen(false);
            handleAuthCheck(0, foundSubdomain);
          }
        }, 500);

        toast.info("Please complete Zendesk authentication in the popup window.", {
          theme: "colored",
          autoClose: 5000,
        });
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.message || "Login failed. Please try again.", {
        theme: "colored",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle auth check after popup closes with retry logic
  const handleAuthCheck = async (retryCount = 0, subdomainToCheck = null) => {
    const maxRetries = 5;
    const retryDelay = 1500; // 1.5 seconds between retries

    setIsLoading(true);

    // Wait a bit for backend to process the OAuth callback
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      // Pass subdomain directly to avoid state timing issues
      const authResult = await checkAuthenticationStatus(subdomainToCheck);
      if (authResult) {
        toast.success("Authentication successful! Redirecting...", { theme: "colored" });
        setTimeout(() => navigate("/dashboard"), 1000);
      } else if (retryCount < maxRetries) {
        // Retry after delay
        console.log(`Auth check retry ${retryCount + 1}/${maxRetries}`);
        setTimeout(() => handleAuthCheck(retryCount + 1, subdomainToCheck), retryDelay);
      } else {
        setIsLoading(false);
        toast.info("Authentication may still be processing. Please try logging in again.", { theme: "colored" });
      }
    } catch (error) {
      console.error("Auth check error:", error);
      if (retryCount < maxRetries) {
        setTimeout(() => handleAuthCheck(retryCount + 1, subdomainToCheck), retryDelay);
      } else {
        setIsLoading(false);
        toast.error("Authentication check failed. Please try again.", { theme: "colored" });
      }
    }
  };

  // AI Features data
  const aiFeatures = [
    {
      icon: faBrain,
      title: "AI Sentiment Analysis",
      description: "Automatically analyze customer feedback sentiment with advanced NLP algorithms.",
    },
    {
      icon: faChartPie,
      title: "Smart Analytics",
      description: "Get actionable insights with AI-powered trend detection and anomaly alerts.",
    },
    {
      icon: faLightbulb,
      title: "Intelligent Suggestions",
      description: "Receive AI-generated recommendations to improve customer satisfaction.",
    },
    {
      icon: faSync,
      title: "Real-time Processing",
      description: "Process and analyze feedback in real-time as it comes in from Zendesk.",
    },
  ];

  // How it works steps
  const howItWorks = [
    {
      step: 1,
      title: "Register as Zendesk Admin",
      description: "Sign up with your Zendesk admin account using secure OAuth authentication.",
    },
    {
      step: 2,
      title: "Select or Create Template",
      description: "Choose from default CSAT/NPS templates or create your own custom survey template.",
    },
    {
      step: 3,
      title: "Assign Zendesk Groups",
      description: "Assign survey templates to your Zendesk support groups. That's it - you're done!",
    },
    {
      step: 4,
      title: "Automatic Survey Emails",
      description: "When a ticket from an assigned group is closed, an automated survey email is sent to the requester.",
    },
    {
      step: 5,
      title: "Collect Feedback",
      description: "Customers rate the agent who handled their ticket. Feedback is linked to the specific ticket and agent.",
    },
    {
      step: 6,
      title: "Dashboard & Reports",
      description: "View all survey results on your dashboard. Generate custom reports and email them to yourself or your team.",
    },
  ];

  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center py-4">
            {/* Left Side - Branding & Info */}
            <Col lg={6} className="mb-5 mb-lg-0">
              <div className="hero-content">
                <img src={logo} alt="TopCX Survey" className="hero-logo mb-3" />
                <h1 className="hero-title">
                  <span className="gradient-text">TopCX Survey</span>
                </h1>
                <h3 className="hero-tagline">AI-Powered Customer Feedback for Zendesk</h3>
                <p className="hero-subtitle">
                  Automatically send CSAT surveys when Zendesk tickets are closed.
                  Collect customer feedback, track agent performance, and gain AI-powered insights.
                </p>

                <div className="hero-badges">
                  <div className="badge-item">
                    <FontAwesomeIcon icon={faCheckCircle} className="badge-icon" />
                    <span>Zendesk Native Integration</span>
                  </div>
                  <div className="badge-item">
                    <FontAwesomeIcon icon={faRobot} className="badge-icon" />
                    <span>AI-Powered Analytics</span>
                  </div>
                  <div className="badge-item free-badge">
                    <FontAwesomeIcon icon={faStar} className="badge-icon" />
                    <span>Free up to {freeTierLimit} surveys/month</span>
                  </div>
                </div>
              </div>
            </Col>

            {/* Right Side - Auth Card */}
            <Col lg={6}>
              <Card className="auth-card">
                <Card.Body className="p-4 p-md-5">
                  {popupOpen ? (
                    <div className="text-center py-5">
                      <div className="popup-indicator mb-4">
                        <Spinner animation="border" variant="primary" />
                      </div>
                      <h4>Zendesk Authentication in Progress</h4>
                      <p className="text-muted">
                        Please complete the login in the popup window.
                        This page will update automatically.
                      </p>
                    </div>
                  ) : (
                    <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                      <Nav variant="pills" className="auth-tabs mb-4">
                        <Nav.Item>
                          <Nav.Link eventKey="login">Login</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="register">Register</Nav.Link>
                        </Nav.Item>
                      </Nav>

                      <Tab.Content>
                        {/* Login Tab */}
                        <Tab.Pane eventKey="login">
                          <h4 className="mb-3">Welcome Back</h4>
                          <p className="text-muted mb-4">
                            Login with your registered email address
                          </p>

                          <Form onSubmit={handleLoginSubmit}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                                Email Address
                              </Form.Label>
                              <Form.Control
                                type="email"
                                placeholder="your@email.com"
                                value={loginForm.email}
                                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                                disabled={isLoading}
                              />
                            </Form.Group>

                            {loginForm.email && (
                              <div className="subdomain-lookup mb-3">
                                {lookingUpEmail ? (
                                  <small className="text-muted">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Looking up account...
                                  </small>
                                ) : foundSubdomain ? (
                                  <Alert variant="success" className="py-2 mb-0">
                                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                    Found: <strong>{foundSubdomain}.zendesk.com</strong>
                                  </Alert>
                                ) : loginForm.email.includes("@") ? (
                                  <Alert variant="warning" className="py-2 mb-0">
                                    No account found. <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab("register"); }}>Register here</a>
                                  </Alert>
                                ) : null}
                              </div>
                            )}

                            <Button
                              type="submit"
                              className="w-100 btn-primary-gradient"
                              disabled={isLoading || !foundSubdomain}
                            >
                              {isLoading ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Connecting...
                                </>
                              ) : (
                                <>
                                  Login with Zendesk
                                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                                </>
                              )}
                            </Button>
                          </Form>

                          <div className="text-center mt-4">
                            <small className="text-muted">
                              Don't have an account?{" "}
                              <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab("register"); }}>
                                Register now
                              </a>
                            </small>
                          </div>
                        </Tab.Pane>

                        {/* Register Tab */}
                        <Tab.Pane eventKey="register">
                          <h4 className="mb-3">Create Account</h4>

                          <Alert variant="info" className="mb-4">
                            <FontAwesomeIcon icon={faUserShield} className="me-2" />
                            <strong>Zendesk Admin Required:</strong> You must be a Zendesk admin to register.
                          </Alert>

                          <Form onSubmit={handleRegisterSubmit}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                <FontAwesomeIcon icon={faGlobe} className="me-2" />
                                Zendesk Domain
                              </Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type="text"
                                  placeholder="your-company"
                                  value={registerForm.zendeskSubdomain}
                                  onChange={(e) => {
                                    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                                    setRegisterForm({ ...registerForm, zendeskSubdomain: value });
                                  }}
                                  disabled={isLoading}
                                />
                                <InputGroup.Text className="zendesk-suffix">.zendesk.com</InputGroup.Text>
                              </InputGroup>
                              <Form.Text className="text-muted">
                                Enter your Zendesk subdomain (e.g., "company" from company.zendesk.com)
                              </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>
                                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                                Email Address
                              </Form.Label>
                              <Form.Control
                                type="email"
                                placeholder="admin@yourcompany.com"
                                value={registerForm.email}
                                onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                                disabled={isLoading}
                              />
                            </Form.Group>

                            <Form.Group className="mb-4">
                              <Form.Label>
                                <FontAwesomeIcon icon={faPhone} className="me-2" />
                                Phone Number
                              </Form.Label>
                              <InputGroup>
                                <Form.Select
                                  style={{ maxWidth: "110px" }}
                                  value={registerForm.countryCode}
                                  onChange={(e) => setRegisterForm({ ...registerForm, countryCode: e.target.value })}
                                  disabled={isLoading}
                                >
                                  {countryCodes.map((cc) => (
                                    <option key={cc.code} value={cc.code}>
                                      {cc.code} {cc.country}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control
                                  type="tel"
                                  placeholder="Phone number"
                                  value={registerForm.phoneNumber}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");
                                    setRegisterForm({ ...registerForm, phoneNumber: value });
                                  }}
                                  disabled={isLoading}
                                />
                              </InputGroup>
                            </Form.Group>

                            <Alert variant="light" className="register-note mb-4">
                              <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />
                              After clicking Register, a <strong>Zendesk login window</strong> will open.
                              Please login with your <strong>Zendesk admin credentials</strong> to complete registration.
                            </Alert>

                            <div className="free-tier-badge mb-4">
                              <FontAwesomeIcon icon={faRocket} className="me-2" />
                              <span>Free tier includes <strong>{freeTierLimit} surveys/month</strong></span>
                            </div>

                            <Button
                              type="submit"
                              className="w-100 btn-primary-gradient"
                              disabled={isLoading || !registerForm.zendeskSubdomain || !registerForm.email || !registerForm.phoneNumber}
                            >
                              {isLoading ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  Register & Connect Zendesk
                                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                                </>
                              )}
                            </Button>
                          </Form>

                          <div className="text-center mt-4">
                            <small className="text-muted">
                              Already have an account?{" "}
                              <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab("login"); }}>
                                Login here
                              </a>
                            </small>
                          </div>
                        </Tab.Pane>
                      </Tab.Content>
                    </Tab.Container>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">How TopCX Survey Works</h2>
            <p className="section-subtitle">
              Get started in minutes - it's simple and fully automated
            </p>
          </div>

          <Row>
            {howItWorks.map((item, index) => (
              <Col md={6} lg={4} key={index} className="mb-4">
                <div className="how-card">
                  <div className="step-number">{item.step}</div>
                  <h5>{item.title}</h5>
                  <p>{item.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* AI Features Section */}
      <section className="ai-features-section py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">AI-Powered Features</h2>
            <p className="section-subtitle">
              TopCX Survey uses cutting-edge AI to help you understand your customers better
            </p>
          </div>

          <Row>
            {aiFeatures.map((feature, index) => (
              <Col md={6} lg={3} key={index} className="mb-4">
                <Card className="ai-feature-card h-100">
                  <Card.Body className="text-center p-4">
                    <div className="feature-icon-wrapper mb-3">
                      <FontAwesomeIcon icon={feature.icon} className="feature-icon" />
                    </div>
                    <h5>{feature.title}</h5>
                    <p className="text-muted mb-0">{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Zendesk Integration Section */}
      <section className="zendesk-section py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="section-title">Native Zendesk Integration</h2>
              <p className="mb-4">
                TopCX Survey seamlessly integrates with your Zendesk Support instance.
                When tickets are closed, survey emails are automatically sent to customers
                requesting feedback on the agent who handled their ticket.
              </p>
              <ul className="integration-features">
                <li>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                  One-click OAuth setup - connect in seconds
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                  Automatic survey emails when tickets are closed
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                  Feedback linked to tickets and agents for easy tracking
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                  Generate custom reports and email them to your team
                </li>
              </ul>
            </Col>
            <Col lg={6}>
              <div className="zendesk-visual">
                <div className="visual-card">
                  <FontAwesomeIcon icon={faComments} className="visual-icon" />
                  <span>Customer Feedback</span>
                </div>
                <div className="visual-arrow">
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
                <div className="visual-card highlight">
                  <FontAwesomeIcon icon={faBrain} className="visual-icon" />
                  <span>AI Analysis</span>
                </div>
                <div className="visual-arrow">
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
                <div className="visual-card">
                  <FontAwesomeIcon icon={faChartLine} className="visual-icon" />
                  <span>Actionable Insights</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <div className="cta-content text-center">
            <h2>Start Using TopCX Survey Today</h2>
            <p className="mb-4">
              Get <strong>{freeTierLimit} surveys free</strong> every month. No credit card required.
            </p>
            <Button
              className="btn-primary-gradient btn-lg"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setActiveTab("register");
              }}
            >
              <FontAwesomeIcon icon={faRocket} className="me-2" />
              Get Started Free
            </Button>
          </div>
        </Container>
      </section>

      {/* Support Section */}
      <section className="support-section py-4">
        <Container>
          <div className="text-center">
            <div className="support-content">
              <FontAwesomeIcon icon={faEnvelope} className="support-icon me-2" />
              <span>Need help? Contact us at </span>
              <a href="mailto:support@TopCx.ai" className="support-email">support@TopCx.ai</a>
            </div>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer className="homepage-footer py-4">
        <Container>
          <div className="text-center text-muted">
            <p className="mb-0">
              &copy; {new Date().getFullYear()} TopCX Survey by Pytron Inc. All rights reserved.
            </p>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default HomePage;
